import type { Account, Studio } from "@glambyzra/shared";

const sessionKey = "glambyzra_session";
const readSession = (): { accessToken?: string } | null => {
  try {
    return JSON.parse(localStorage.getItem(sessionKey) ?? "null") as { accessToken?: string } | null;
  } catch {
    return null;
  }
};
const authHeaders = (): Record<string, string> => {
  const token = readSession()?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type ApiEnvelope<T> = { success: true; data: T };

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// The API speaks two error shapes: `{ success: false, errors: [{ messages }] }` from
// `translateError`, and `errors: { field: [message] }` (the flattened field map a
// validation failure carries) from the zod hook.
const errorText = (body: { error?: string; errors?: unknown } | null): string | undefined => {
  const errors = body?.errors;
  if (Array.isArray(errors)) {
    const messages = errors.flatMap((entry) =>
      typeof entry === "object" && entry !== null && "messages" in entry && Array.isArray(entry.messages)
        ? (entry.messages as string[])
        : [],
    );
    if (messages.length) return [...new Set(messages)].join(", ");
  } else if (errors && typeof errors === "object") {
    const messages = Object.values(errors).flatMap((v) => (Array.isArray(v) ? v.filter((m): m is string => typeof m === "string") : []));
    if (messages.length) return [...new Set(messages)].join(", ");
  }
  return body?.error;
};

const parse = async <T>(response: Response): Promise<T> => {
  const body = (await response.json().catch(() => null)) as { success?: boolean; data?: T; error?: string; errors?: unknown } | null;
  if (!response.ok || !body?.success) {
    throw new ApiError(errorText(body) || "Request failed", response.status);
  }
  return body.data as T;
};

/** Called once when a request keeps failing on 401 after a refresh attempt. */
let onSessionExpired: (() => void) | null = null;
export const setSessionExpiredHandler = (handler: (() => void) | null): void => {
  onSessionExpired = handler;
};

// ── Silent refresh ─────────────────────────────────────
// The access token lives 90 minutes. A 401 means "expired", not "signed out":
// renew once with the refresh cookie, replay the request, and only give up when
// the refresh itself fails. One attempt is kept per token, so concurrent 401s share
// it — a *failed* attempt included. (The old version dropped the promise as soon as
// it settled, so a dead session refreshed once per query: the AppShell notification
// query turned that into `refreshes=2`.) A new session carries a new token, which
// starts a new attempt.

let attemptedToken: string | null = null;
let refreshing: Promise<boolean> | null = null;

const refreshSession = (sent: string | null): Promise<boolean> => {
  if (sent !== null && attemptedToken !== sent) {
    attemptedToken = sent;
    refreshing = null;
  }
  refreshing ??= (async () => {
    try {
      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const body = (await response.json().catch(() => null)) as { success?: boolean; data?: { accessToken?: string } } | null;
      const next = body?.data;
      if (!response.ok || !body?.success || !next?.accessToken) return false;
      // Keep the stored session in step; authHeaders() reads the token per request.
      const current = readSession();
      if (current) localStorage.setItem(sessionKey, JSON.stringify({ ...current, ...next }));
      return true;
    } catch {
      return false;
    }
  })();
  return refreshing;
};

type JsonInit = Omit<RequestInit, "headers"> & { headers?: Record<string, string> };

async function sendRaw(path: string, init: JsonInit, token?: string): Promise<Response> {
  const call = (bearer?: string) =>
    fetch(path, {
      ...init,
      credentials: "include",
      headers: { ...init.headers, ...(bearer ? { Authorization: `Bearer ${bearer}` } : authHeaders()) },
    });

  let response = await call(token);
  // The refresh endpoint is the last resort: never refresh a refresh.
  if (response.status === 401 && !path.endsWith("/api/auth/refresh")) {
    // A request that started before a parallel refresh landed still holds the old token:
    // replay it with the new one instead of refreshing a second time.
    const sent = token ?? readSession()?.accessToken;
    const fresh = readSession()?.accessToken !== sent || (await refreshSession(sent ?? null));
    if (fresh) response = await call();
    if (response.status === 401) {
      localStorage.removeItem(sessionKey);
      onSessionExpired?.();
    }
  }
  return response;
}

async function send<T>(path: string, init: JsonInit, token?: string): Promise<T> {
  return parse<T>(await sendRaw(path, init, token));
}

export const api = {
  get<T>(path: string, token?: string): Promise<T> {
    return send<T>(path, { method: "GET" }, token);
  },
  post<T = unknown>(path: string, payload: unknown): Promise<T> {
    return send<T>(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  },
  postForm<T = unknown>(path: string, payload: FormData): Promise<T> {
    return send<T>(path, { method: "POST", body: payload });
  },
  put<T = unknown>(path: string, payload: unknown): Promise<T> {
    return send<T>(path, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  },
  patch<T = unknown>(path: string, payload: unknown): Promise<T> {
    return send<T>(path, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  },
  delete<T = unknown>(path: string): Promise<T> {
    return send<T>(path, { method: "DELETE" });
  },
  /** Binary download (the `.xlsx` exports): the bytes plus the server's filename. */
  async download(path: string): Promise<{ blob: Blob; filename: string }> {
    const response = await sendRaw(path, { method: "GET" });
    // A failed export answers the JSON envelope, so `parse` turns it into the API's own words.
    if (!response.ok) return parse<never>(response);
    const disposition = response.headers.get("Content-Disposition") ?? "";
    return {
      blob: await response.blob(),
      filename: /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? "export.xlsx",
    };
  },
};

export type { Account, Studio };
